import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  reportingApi,
  ReportTemplate,
  ReportExecution,
  ReportFormat,
  ReportType
} from '../api';
import {
  FileText,
  Download,
  Clock,
  Play,
  CheckCircle,
  X,
  RefreshCw,
  AlertTriangle,
  BarChart3,
  File,
  Settings,
  Plus,
  Eye,
  Send,
  Archive,
  Zap
} from 'lucide-react';

interface ReportGenerationRequest {
  reportId: string;
  userId?: number;
  parameters?: Record<string, any>;
}

const ReportingManager: React.FC = () => {
  const { hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState<'templates' | 'generate' | 'history' | 'schedule'>('templates');
  const [reportTemplates, setReportTemplates] = useState<ReportTemplate[]>([]);
  const [generatedReports, setGeneratedReports] = useState<ReportExecution[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<ReportTemplate | null>(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationRequest, setGenerationRequest] = useState<ReportGenerationRequest>({
    reportId: '',
    parameters: {}
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = useCallback(async () => {
    try {
      const templates = await reportingApi.getAllTemplates();
      const reports = await reportingApi.getAllExecutions();

      setReportTemplates(templates);
      setGeneratedReports(reports);
    } catch (error) {
      console.error('Failed to load reporting data:', error);
    }
  }, []);

  const handleGenerateReport = useCallback(async () => {
    if (!generationRequest.reportId) {
      alert('Please select a report template');
      return;
    }

    setIsGenerating(true);
    try {
      const report = await reportingApi.executeReport(generationRequest);

      // Update local state
      setGeneratedReports(prev => [report, ...prev]);
      setShowGenerateModal(false);

      // Auto-download the report
      setTimeout(() => {
        reportingApi.downloadReport(report.id);
      }, 1000);

      alert('Report generated successfully!');
    } catch (error) {
      console.error('Failed to generate report:', error);
      alert('Failed to generate report. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }, [generationRequest]);

  const handleDownloadReport = useCallback(async (reportId: string) => {
    try {
      const blob = await reportingApi.downloadReport(reportId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report_${reportId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download report:', error);
      alert('Failed to download report');
    }
  }, []);

  const handleScheduleReport = useCallback(async (templateId: string) => {
    try {
      // Note: Scheduling functionality would need to be implemented in the API
      // For now, we'll show a message indicating this feature
      console.log('Schedule report for template:', templateId);
      alert('Report scheduling feature is not yet implemented. Please contact your administrator.');
    } catch (error) {
      console.error('Failed to schedule report:', error);
      alert('Failed to schedule report');
    }
  }, []);

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      running: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getFormatIcon = (format: ReportFormat) => {
    const icons: Record<ReportFormat, React.ReactNode> = {
      pdf: <FileText className="text-red-600" size={20} />,
      excel: <BarChart3 className="text-green-600" size={20} />,
      csv: <File className="text-blue-600" size={20} />,
      html: <FileText className="text-purple-600" size={20} />,
      json: <File className="text-slate-600" size={20} />
    };
    return icons[format] || <File className="text-slate-600" size={20} />;
  };

  const getTypeColor = (type: ReportType) => {
    const colors: Record<string, string> = {
      compliance_summary: 'bg-red-100 text-red-800',
      compliance_detailed: 'bg-blue-100 text-blue-800',
      audit_trail: 'bg-green-100 text-green-800',
      risk_assessment: 'bg-purple-100 text-purple-800',
      performance_metrics: 'bg-yellow-100 text-yellow-800',
      user_activity: 'bg-indigo-100 text-indigo-800',
      framework_compliance: 'bg-pink-100 text-pink-800',
      custom: 'bg-gray-100 text-gray-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const f = (bytes / Math.pow(k, i)).toFixed(2);

    return parseFloat(f) + ' ' + sizes[i];
  };

  if (!hasPermission('reports.view')) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
          <div className="bg-red-600 px-8 py-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                <AlertTriangle className="text-white" size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Access Denied</h2>
                <p className="text-red-100 text-sm mt-0.5">Restricted Area</p>
              </div>
            </div>
          </div>
          <div className="p-8">
            <p className="text-slate-600">Only authorized users can access the Reporting Management system.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-6">
          <div className="bg-blue-600 px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                  <FileText className="text-white" size={32} />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">Automated Reporting System</h1>
                  <p className="text-indigo-100 mt-1">Generate, schedule, and manage compliance reports in multiple formats</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setShowGenerateModal(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-indigo-600 font-medium rounded-xl hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-indigo-600 transition-all shadow-lg"
                >
                  <Plus size={20} />
                  Generate Report
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-6">
          <div className="border-b border-slate-100">
            <nav className="flex -mb-px">
              {[
                { id: 'templates', label: 'Report Templates', icon: FileText, color: 'text-slate-600' },
                { id: 'generate', label: 'Generate Report', icon: Zap, color: 'text-blue-600' },
                { id: 'history', label: 'Report History', icon: Archive, color: 'text-green-600' },
                { id: 'schedule', label: 'Scheduled Reports', icon: Clock, color: 'text-purple-600' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center gap-2 py-4 px-6 border-b-2 font-medium text-sm transition-all ${
                    activeTab === tab.id
                      ? 'border-indigo-500 text-indigo-600 bg-indigo-50'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                  }`}
                >
                  <tab.icon size={18} className={activeTab === tab.id ? 'text-indigo-600' : tab.color} />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          {/* Report Templates Tab */}
          {activeTab === 'templates' && (
            <div className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <FileText className="text-slate-600" size={24} />
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Report Templates</h2>
                  <p className="text-sm text-slate-600">Pre-configured templates for different reporting needs</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {reportTemplates.map((template) => (
                  <div key={template.id} className="p-6 rounded-xl border border-slate-200 hover:shadow-lg transition-all hover:border-indigo-300">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-100 rounded-lg">
                          {getFormatIcon('pdf' as ReportFormat)}
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{template.name}</h3>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-1 ${getTypeColor(template.type)}`}>
                            {template.type}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <span>Variables:</span>
                        <span className="font-medium">{template.variables?.length || 0}</span>
                      </div>
                    </div>

                    <div className="text-xs text-slate-400 mb-4">
                      Created: {new Date(template.createdAt).toLocaleDateString()}
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setSelectedTemplate(template);
                          setGenerationRequest(prev => ({ ...prev, reportId: template.id }));
                        }}
                        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all shadow-sm"
                      >
                        <Eye size={16} />
                        Use Template
                      </button>
                      <button
                        onClick={() => handleScheduleReport(template.id)}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white font-medium rounded-xl hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-all shadow-sm"
                      >
                        <Clock size={16} />
                        Schedule
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Generate Report Tab */}
          {activeTab === 'generate' && (
            <div className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <Zap className="text-blue-600" size={24} />
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Generate New Report</h2>
                  <p className="text-sm text-slate-600">Create reports from templates with custom options</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">Report Template</label>
                  <select
                    value={generationRequest.reportId}
                    onChange={(e) => setGenerationRequest(prev => ({ ...prev, reportId: e.target.value }))}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all appearance-none bg-white cursor-pointer"
                  >
                    <option value="">Select a template...</option>
                    {reportTemplates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {selectedTemplate && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6 mb-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Eye className="text-indigo-600" size={20} />
                    <h3 className="font-semibold text-indigo-900">Template Preview</h3>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="bg-white p-3 rounded-lg">
                      <span className="text-indigo-600 font-medium">Variables:</span>
                      <span className="ml-2 text-slate-900">{selectedTemplate.variables?.length || 0}</span>
                    </div>
                    <div className="bg-white p-3 rounded-lg">
                      <span className="text-indigo-600 font-medium">Type:</span>
                      <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(selectedTemplate.type)}`}>
                        {selectedTemplate.type}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-center">
                <button
                  onClick={() => setShowGenerateModal(true)}
                  disabled={!generationRequest.reportId || isGenerating}
                  className="inline-flex items-center gap-2 px-8 py-4 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
                >
                  {isGenerating ? (
                    <>
                      <RefreshCw size={20} className="animate-spin" />
                      Generating Report...
                    </>
                  ) : (
                    <>
                      <Play size={20} />
                      Generate Report
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Report History Tab */}
          {activeTab === 'history' && (
            <div className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <Archive className="text-green-600" size={24} />
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Report History</h2>
                  <p className="text-sm text-slate-600">View and download previously generated reports</p>
                </div>
              </div>

              {generatedReports.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <Archive className="text-slate-400 mb-4" size={64} />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No reports generated</h3>
                  <p className="text-slate-600 max-w-md">Generated reports will appear here for download and review.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-100">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Report ID</th>
                        <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Format</th>
                        <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">File Size</th>
                        <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Generated</th>
                        <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-100">
                      {generatedReports.map((report) => (
                        <tr key={report.id} className="hover:bg-slate-50">
                          <td className="px-6 py-4 whitespace-nowrap text-left">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-slate-100 rounded-lg">
                                {getFormatIcon(report.format as ReportFormat)}
                              </div>
                              <span className="text-sm font-medium text-slate-900">{report.reportId}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-left">
                            <span className="text-sm text-slate-900 font-medium">{report.format.toUpperCase()}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-left text-sm text-slate-900">
                            {formatFileSize(report.fileSize || 0)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-left text-sm text-slate-900">
                            {new Date(report.executedAt).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-left">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(report.status)}`}>
                              {report.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium">
                            {report.status === 'completed' ? (
                              <button
                                onClick={() => handleDownloadReport(report.id)}
                                className="inline-flex items-center gap-2 px-3 py-1 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all"
                              >
                                <Download size={14} />
                                Download
                              </button>
                            ) : report.errorMessage ? (
                              <div className="text-red-600 text-xs max-w-xs truncate" title={report.errorMessage}>
                                Error: {report.errorMessage}
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 text-slate-500 text-xs">
                                <RefreshCw size={14} className="animate-spin" />
                                Processing...
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Scheduled Reports Tab */}
          {activeTab === 'schedule' && (
            <div className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <Clock className="text-purple-600" size={24} />
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Scheduled Reports</h2>
                  <p className="text-sm text-slate-600">Configure automated report generation and distribution</p>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
                <div className="flex items-center gap-3 mb-3">
                  <Settings className="text-amber-600" size={20} />
                  <h3 className="font-semibold text-amber-900">Automated Report Scheduling</h3>
                </div>
                <p className="text-sm text-amber-800">
                  Configure automated report generation and distribution schedules.
                  Reports can be generated daily, weekly, monthly, or quarterly with custom recipients and formats.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {reportTemplates.map((template) => (
                  <div key={template.id} className="p-6 rounded-xl border border-slate-200 hover:shadow-md transition-all">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-100 rounded-lg">
                          {getFormatIcon('pdf' as ReportFormat)}
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{template.name}</h3>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-1 ${getTypeColor(template.type)}`}>
                            {template.type}
                          </span>
                        </div>
                      </div>
                      <span className="text-xs text-slate-500 font-medium">
                        PDF
                      </span>
                    </div>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-slate-700">Schedule Frequency</label>
                        <select className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all appearance-none bg-white cursor-pointer text-sm">
                          <option value="daily">Daily at 09:00</option>
                          <option value="weekly">Weekly on Monday at 09:00</option>
                          <option value="monthly">Monthly on 1st at 09:00</option>
                          <option value="quarterly">Quarterly on 1st at 09:00</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-slate-700">Recipients</label>
                        <input
                          type="email"
                          placeholder="admin@company.com, manager@company.com"
                          className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-sm"
                        />
                      </div>
                    </div>

                    <div className="flex gap-3 mt-6">
                      <button
                        onClick={() => handleScheduleReport(template.id)}
                        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white font-medium rounded-xl hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-all shadow-sm"
                      >
                        <Send size={16} />
                        Save Schedule
                      </button>
                      <button
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-slate-600 text-white font-medium rounded-xl hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 transition-all shadow-sm"
                      >
                        <Play size={16} />
                        Test Run
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Generate Report Modal */}
        {showGenerateModal && (
          <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm overflow-y-auto h-full w-full z-[9999]">
            <div className="relative min-h-full flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full">
                {/* Modal Header */}
                <div className="bg-blue-600 px-8 py-6 sticky top-0 z-10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                        <Zap className="text-white" size={24} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Generate Report</h3>
                        <p className="text-indigo-100 text-sm mt-0.5">Create a new report from the selected template</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowGenerateModal(false)}
                      className="p-2 bg-white/20 hover:bg-white/30 rounded-xl backdrop-blur-sm transition-colors"
                    >
                      <X className="text-white" size={24} />
                    </button>
                  </div>
                </div>

                {/* Modal Body */}
                <div className="p-8 space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-slate-700">Template</label>
                      <select
                        value={generationRequest.reportId}
                        onChange={(e) => setGenerationRequest(prev => ({ ...prev, reportId: e.target.value }))}
                        className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all appearance-none bg-white cursor-pointer"
                      >
                        <option value="">Select a template...</option>
                        {reportTemplates.map(template => (
                          <option key={template.id} value={template.id}>
                            {template.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="bg-slate-50 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Settings className="text-slate-600" size={20} />
                      <h4 className="font-semibold text-slate-900">Report Options</h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          defaultChecked={false}
                          className="h-5 w-5 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500 focus:ring-offset-0"
                        />
                        <span className="text-sm font-medium text-slate-700">Include draft records</span>
                      </label>

                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          defaultChecked={false}
                          className="h-5 w-5 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500 focus:ring-offset-0"
                        />
                        <span className="text-sm font-medium text-slate-700">Add watermark</span>
                      </label>

                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          defaultChecked={false}
                          className="h-5 w-5 text-indigo-600 rounded focus:ring-2 focus:ring-indigo-500 focus:ring-offset-0"
                        />
                        <span className="text-sm font-medium text-slate-700">Digital signature</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="px-8 py-6 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
                  <button
                    onClick={() => setShowGenerateModal(false)}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-slate-200 text-slate-700 font-medium rounded-xl hover:bg-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleGenerateReport}
                    disabled={!generationRequest.reportId || isGenerating}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-medium rounded-xl hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                  >
                    {isGenerating ? (
                      <>
                        <RefreshCw size={18} className="animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={18} />
                        Generate Report
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportingManager;
